import React, { useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Button, Text, TextInput } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

import { ResumeLanguageItem } from '../../../types';
import { Palette, radius, spacing, useThemePalette } from '../../../theme';
import RepeatableItemCard from '../RepeatableItemCard';

interface Props {
  value: ResumeLanguageItem[];
  onChange: (next: ResumeLanguageItem[]) => void;
}

const PROFICIENCY_KEYS = ['basic', 'conversational', 'fluent', 'native'] as const;

const LanguagesForm: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);

  const update = (idx: number, patch: Partial<ResumeLanguageItem>) =>
    onChange(value.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const remove = (idx: number) =>
    onChange(value.filter((_, i) => i !== idx));

  return (
    <View style={styles.container}>
      {value.map((item, idx) => (
        <RepeatableItemCard
          key={idx}
          title={item.name || `${t('resume.sections.languages')} ${idx + 1}`}
          onRemove={() => remove(idx)}
        >
          <TextInput
            mode="outlined"
            label={t('resume.fields.languageName')}
            value={item.name}
            onChangeText={(v) => update(idx, { name: v })}
            dense
          />
          <Text style={styles.label}>{t('resume.fields.languageProficiency')}</Text>
          <View style={styles.chipRow}>
            {PROFICIENCY_KEYS.map((key) => {
              const label = t(`resume.proficiency.${key}`);
              const selected = item.proficiency === label;
              return (
                <TouchableOpacity
                  key={key}
                  onPress={() => update(idx, { proficiency: selected ? '' : label })}
                  style={[styles.chip, selected && styles.chipSelected]}
                  activeOpacity={0.85}
                >
                  <Text
                    style={[styles.chipText, selected && styles.chipTextSelected]}
                  >
                    {label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </RepeatableItemCard>
      ))}
      <Button
        mode="outlined"
        icon="plus"
        onPress={() => onChange([...value, { name: '', proficiency: '' }])}
        textColor={palette.textPrimary}
        style={styles.addBtn}
      >
        {t('resume.buttons.addLanguage')}
      </Button>
    </View>
  );
};

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    container: { gap: spacing.sm },
    label: {
      fontSize: 12,
      fontWeight: '600',
      color: p.textMuted,
      marginTop: spacing.xs,
    },
    chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
    chip: {
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.xs + 2,
      borderRadius: radius.pill,
      backgroundColor: p.surface,
      borderWidth: 1,
      borderColor: p.border,
    },
    chipSelected: { backgroundColor: p.appliedBg, borderColor: p.appliedBg },
    chipText: { color: p.textPrimary, fontSize: 13 },
    chipTextSelected: { color: p.appliedText, fontWeight: '700' },
    addBtn: { borderRadius: 999, borderColor: p.border, marginTop: spacing.xs },
  });

export default LanguagesForm;
